import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, StatCard, Button, Modal, Input, Select } from "@/components/ui";
import { money } from "@/lib/utils";
import { toast } from "sonner";
import { Server, Globe, Users, DollarSign, Plus, Film, RefreshCw, CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const STATUS_TONE = { Live: "Active", Implementation: "In Progress", Contracting: "Rising", Discovery: "Pending", Prospect: "Pending" };
const ALL_MODULES = ["Care Management", "Risk Stratification", "Assessments", "Care Plans", "Utilization Mgmt", "Quality Mgmt", "Billing", "AI Suite", "Interoperability", "Member Portal", "Provider Portal", "Contact Center"];
const VOICES = ["shimmer", "nova", "alloy", "fable", "echo", "onyx"];

export default function Tenants() {
  const [tenants, setTenants] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", region: "", plan_types: "", members_count: "", pmpm: "4.25", status: "Prospect" });
  // video management
  const [videoTenant, setVideoTenant] = useState(null);
  const [vForm, setVForm] = useState({ avatar_base64: "", avatar_name: "", narration: "", voice: "shimmer" });
  const [vbusy, setVbusy] = useState(false);
  const pollRef = useRef(null);

  const load = () => api.get("/tenants").then((r) => setTenants(r.data));
  useEffect(() => { load(); }, []);

  // Poll while any tenant video is processing
  useEffect(() => {
    const processing = (tenants || []).some((t) => t.video_status === "processing");
    if (processing && !pollRef.current) {
      pollRef.current = setInterval(load, 6000);
    } else if (!processing && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current && !processing) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [tenants]);

  const changeStatus = async (id, status) => {
    await api.patch(`/tenants/${id}`, { status });
    toast.success(`Tenant moved to ${status}`);
    load();
  };

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/tenants", {
      name: form.name, region: form.region,
      plan_types: form.plan_types.split(",").map((s) => s.trim()).filter(Boolean),
      members_count: parseInt(form.members_count) || 0, pmpm: parseFloat(form.pmpm) || 4.25,
      modules: ALL_MODULES.slice(0, 6), status: form.status, accreditation: [],
    });
    toast.success("Tenant added to control plane");
    setOpen(false); setForm({ name: "", region: "", plan_types: "", members_count: "", pmpm: "4.25", status: "Prospect" });
    load();
  };

  const openVideo = (t) => {
    setVideoTenant(t);
    setVForm({ avatar_base64: "", avatar_name: "", narration: t.narration || "", voice: "shimmer" });
  };

  const onAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    const reader = new FileReader();
    reader.onload = () => setVForm((f) => ({ ...f, avatar_base64: reader.result, avatar_name: file.name }));
    reader.readAsDataURL(file);
  };

  const regenerate = async () => {
    setVbusy(true);
    try {
      await api.post("/tenant/video/regenerate", {
        avatar_base64: vForm.avatar_base64 || null,
        narration: vForm.narration || null,
        voice: vForm.voice,
      }, { headers: { "X-Tenant-Id": videoTenant.id } });
      toast.success("Video generation started — this takes ~1–2 minutes.");
      setVideoTenant(null);
      load();
    } catch { toast.error("Could not start video generation"); } finally { setVbusy(false); }
  };

  if (!tenants) return <Spinner />;

  const totalLives = tenants.reduce((s, t) => s + (t.members_count || 0), 0);
  const arr = tenants.filter((t) => t.status === "Live" || t.status === "Implementation").reduce((s, t) => s + t.members_count * t.pmpm * 12, 0);
  const live = tenants.filter((t) => t.status === "Live").length;

  const VideoBadge = ({ t }) => {
    if (t.video_status === "processing") return <span className="flex items-center gap-1 text-xs text-amber-600"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</span>;
    if (t.video_status === "ready") return <span className="flex items-center gap-1 text-xs text-indigo-700"><CheckCircle2 className="h-3.5 w-3.5" /> Custom video ready</span>;
    if (t.video_status === "error") return <span className="flex items-center gap-1 text-xs text-red-600"><AlertCircle className="h-3.5 w-3.5" /> Generation failed</span>;
    return <span className="text-xs text-muted-foreground">Standard video</span>;
  };

  return (
    <div className="vc-fade-up">
      <PageHeader title="Tenant Control Plane" subtitle="Multi-tenant management across MCOs. Productize once, configure many — each tenant is configuration, not a rebuild."
        actions={<Button onClick={() => setOpen(true)} data-testid="add-tenant-btn"><Plus className="h-4 w-4" /> Add Tenant</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Tenants" value={tenants.length} sub={`${live} live`} icon={Server} tone="brand" />
        <StatCard label="Total Managed Lives" value={totalLives.toLocaleString()} icon={Users} tone="blue" />
        <StatCard label="Contracted ARR" value={money(arr)} sub="Live + implementing" icon={DollarSign} tone="brand" />
        <StatCard label="Markets" value={new Set(tenants.map((t) => t.region.split("(")[0].trim())).size} icon={Globe} tone="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {tenants.map((t) => (
          <Card key={t.id} className="p-5" hover data-testid="tenant-card">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-semibold text-lg">{t.name}</h3>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Globe className="h-3.5 w-3.5" />{t.region}</p>
              </div>
              <Badge tone={STATUS_TONE[t.status]}>{t.status}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Members</p><p className="font-mono font-semibold">{t.members_count.toLocaleString()}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">PMPM</p><p className="font-mono font-semibold">${t.pmpm.toFixed(2)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. ARR</p><p className="font-mono font-semibold text-brand-700">{money(t.members_count * t.pmpm * 12)}</p></div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {t.plan_types.map((p) => <span key={p} className="text-[10px] rounded bg-accent px-1.5 py-0.5 text-accent-foreground">{p}</span>)}
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {t.modules.slice(0, 6).map((mo) => <span key={mo} className="text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-muted-foreground">{mo}</span>)}
              {t.modules.length > 6 && <span className="text-[10px] text-muted-foreground">+{t.modules.length - 6} modules</span>}
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <button onClick={() => openVideo(t)} className="flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800 transition-colors" data-testid={`tenant-video-${t.id}`}>
                  <Film className="h-3.5 w-3.5" /> Explainer Video
                </button>
                <VideoBadge t={t} />
              </div>
              <Select value={t.status} onChange={(e) => changeStatus(t.id, e.target.value)} className="h-8 text-xs w-auto" data-testid={`tenant-status-${t.id}`}>
                {["Prospect", "Discovery", "Contracting", "Implementation", "Live"].map((s) => <option key={s}>{s}</option>)}
              </Select>
            </div>
          </Card>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add MCO Tenant">
        <div className="space-y-3">
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organization Name</label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name" /></div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Region</label><Input className="mt-1.5" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g., Ohio (Statewide)" /></div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Plan Types (comma-separated)</label><Input className="mt-1.5" value={form.plan_types} onChange={(e) => setForm({ ...form, plan_types: e.target.value })} placeholder="Tailored Plan, D-SNP" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Members</label><Input className="mt-1.5" type="number" value={form.members_count} onChange={(e) => setForm({ ...form, members_count: e.target.value })} /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">PMPM</label><Input className="mt-1.5" type="number" step="0.05" value={form.pmpm} onChange={(e) => setForm({ ...form, pmpm: e.target.value })} /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label><Select className="w-full mt-1.5" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{["Prospect", "Discovery", "Contracting", "Implementation", "Live"].map((s) => <option key={s}>{s}</option>)}</Select></div>
          </div>
          <Button className="w-full" onClick={create} data-testid="tenant-save">Add Tenant</Button>
        </div>
      </Modal>

      <Modal open={!!videoTenant} onClose={() => setVideoTenant(null)} title={`Explainer Video — ${videoTenant?.name || ""}`}>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Generate a white-labeled product-tour video branded in this tenant's color. Upload a custom presenter avatar and/or a custom voiceover script, then regenerate.
          </p>

          {videoTenant?.video_status === "ready" && videoTenant?.video_url && (
            <video controls playsInline className="w-full rounded-md bg-brand-950" data-testid="tenant-video-preview">
              <source src={videoTenant.video_url} type="video/mp4" />
            </video>
          )}
          {videoTenant?.video_status === "processing" && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800"><Loader2 className="h-4 w-4 animate-spin" /> Generating video… you can close this and check back shortly.</div>
          )}
          {videoTenant?.video_status === "error" && (
            <div className="rounded-md bg-red-50 p-3 text-xs text-red-700">{videoTenant.video_error || "Generation failed."}</div>
          )}

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Presenter Avatar (optional)</label>
            <input type="file" accept="image/*" onChange={onAvatar} data-testid="video-avatar-input"
              className="mt-1.5 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-brand-700 file:font-medium hover:file:bg-brand-100" />
            {vForm.avatar_name && <p className="text-[11px] text-indigo-700 mt-1">Selected: {vForm.avatar_name}</p>}
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Voiceover Script (optional)</label>
            <textarea value={vForm.narration} onChange={(e) => setVForm({ ...vForm, narration: e.target.value })} data-testid="video-narration-input"
              placeholder="Leave blank to use the standard narration script."
              className="mt-1.5 w-full h-32 rounded-md border border-input bg-card text-sm p-3 focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Voice</label>
            <Select className="w-full mt-1.5" value={vForm.voice} onChange={(e) => setVForm({ ...vForm, voice: e.target.value })} data-testid="video-voice-select">
              {VOICES.map((v) => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
            </Select>
          </div>

          <Button className="w-full" onClick={regenerate} disabled={vbusy || videoTenant?.video_status === "processing"} data-testid="video-regenerate-btn">
            <RefreshCw className={`h-4 w-4 ${vbusy ? "animate-spin" : ""}`} /> {vbusy ? "Starting…" : "Regenerate Video"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
